import { createPortal } from 'react-dom';
import { useIsMobile } from '@/hooks';
import { Button } from '@/components/ui/button';
import { useOnboardingStore } from '../onboardingStore';
import { CONTEXTUAL_HINTS } from '../data/contextualHints';
import { useSpotlightPosition } from '../hooks/useSpotlightPosition';
import { OnboardingTooltip } from './OnboardingTooltip';

/**
 * Renders the currently active contextual hint.
 * On desktop: positioned tooltip near the target element.
 * On mobile: bottom toast bar above the mobile nav.
 */
export function ContextualHintRenderer() {
  const activeHint = useOnboardingStore((s) => s.activeHint);
  const dismissHint = useOnboardingStore((s) => s.dismissHint);
  const isMobile = useIsMobile();

  const hintDef = activeHint
    ? CONTEXTUAL_HINTS.find((h) => h.id === activeHint)
    : null;

  const { rect: targetRect } = useSpotlightPosition(
    hintDef?.targetSelector ?? null
  );

  if (!hintDef || !activeHint) return null;

  const handleDismiss = () => {
    dismissHint(activeHint);
  };

  // Mobile: bottom toast bar
  if (isMobile) {
    return createPortal(
      <div
        className="fixed left-4 right-4 bottom-[76px] z-[55] animate-onboarding-in rounded-lg border bg-card p-4 shadow-lg"
        role="status"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <p className="text-sm font-medium">{hintDef.title}</p>
            <p className="mt-0.5 text-sm text-muted-foreground">{hintDef.content}</p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            aria-label="Dismiss hint"
          >
            Got it
          </Button>
        </div>
      </div>,
      document.body
    );
  }

  // Desktop: positioned tooltip
  if (!targetRect) return null;

  return (
    <OnboardingTooltip
      targetRect={targetRect}
      placement={hintDef.placement}
      className="w-[260px]"
    >
      <div className="p-3" role="status">
        <p className="text-sm font-medium">{hintDef.title}</p>
        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{hintDef.content}</p>
        <div className="mt-2.5 flex justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            aria-label="Dismiss hint"
            className="h-7 text-xs"
          >
            Got it
          </Button>
        </div>
      </div>
    </OnboardingTooltip>
  );
}
